import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import SearchableDropdown from '../../../../components/SearchableDropdown';
import usePermissions from '../../../../hook/usePermissions';
import { PERMISSIONS } from '../../../../consts/permissions';
import {
    SOCIETY_PORTAL,
    SOCIETY_PORTAL_VISITS_ADD,
    SOCIETY_PORTAL_VISITS_VIEW,
} from '../../../../Routing/routes';
import { getVisitsList } from '../../../../redux/actions/SocietyPortal/VmsAction/VmsAction';
import useVmsList from '../useVmsList';
import useVisitTransitions from './useVisitTransitions';
import VisitTransitionDialogs from './components/VisitTransitionDialogs';
import { availableActions, canCancelVisit } from './visitActions';
import VmsListShell from '../components/vms/VmsListShell';
import VmsTable from '../components/vms/VmsTable';
import VmsBadge from '../components/vms/VmsBadge';
import WingFlatPicker from '../components/vms/WingFlatPicker';
import { labelCls } from '../components/form/formStyles';
import {
    VISIT_STATUSES,
    VISIT_STATUS_STYLE,
    VISIT_STATUS_ICON,
    PURPOSE_ICON,
    fmtDateTime,
    flatLabel,
    humanize,
    toOptions,
} from '../vmsConstants';
import { fmtPhone } from '../visitorConstants';

const STATUS_OPTIONS = toOptions(VISIT_STATUSES);

const Visits = () => {
    const navigate = useNavigate();
    const { can } = usePermissions();
    const canCreate = can(PERMISSIONS.VMS_VISITOR_CREATE);
    const canUpdate = can(PERMISSIONS.VMS_VISITOR_UPDATE);
    const canDelete = can(PERMISSIONS.VMS_VISITOR_DELETE);

    const list = useVmsList({
        slice: 'visits',
        action: getVisitsList,
        filters: { status: '', wingId: '', flatId: '', fromDate: '', toDate: '' },
    });

    const tx = useVisitTransitions(list.refresh);

    const goView = (v) => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_VISITS_VIEW.replace(':id', v.visit_id));

    const columns = [
        {
            key: 'visit_code',
            header: 'Visit',
            nowrap: true,
            render: (v) => (
                <div>
                    <p className="tw-mb-0 tw-font-mono tw-font-semibold tw-text-gray-800">{v.visit_code || '—'}</p>
                    <p className="tw-mb-0 tw-mt-0.5 tw-text-[10px] tw-text-gray-500">{fmtDateTime(v.created_at)}</p>
                </div>
            ),
        },
        {
            key: 'visitor',
            header: 'Visitor',
            render: (v) => (
                <div className="tw-min-w-0">
                    <p className="tw-mb-0 tw-font-semibold tw-text-gray-800 tw-truncate">
                        {v.visitor?.name || '—'}
                    </p>
                    <p className="tw-mb-0 tw-mt-0.5 tw-text-[10px] tw-text-gray-500 tw-font-mono">
                        {v.visitor?.phone ? fmtPhone(v.visitor.phone) : '—'}
                    </p>
                </div>
            ),
        },
        { key: 'flat', header: 'Flat', nowrap: true, render: (v) => flatLabel(v) },
        {
            key: 'purpose',
            header: 'Purpose',
            render: (v) => (
                <span className="tw-inline-flex tw-items-center tw-gap-1">
                    <Icon icon={PURPOSE_ICON[v.purpose] || PURPOSE_ICON.OTHER} className="tw-text-gray-400" />
                    <span title={v.purpose_detail || ''}>{humanize(v.purpose)}</span>
                </span>
            ),
        },
        {
            key: 'status',
            header: 'Status',
            render: (v) => (
                <VmsBadge
                    value={v.status}
                    styles={VISIT_STATUS_STYLE}
                    icons={VISIT_STATUS_ICON}
                    title={v.status === 'REJECTED' ? v.rejection_reason : undefined}
                />
            ),
        },
        {
            key: 'timing',
            header: 'In / Out',
            nowrap: true,
            render: (v) => (
                <div className="tw-text-[10px] tw-leading-snug">
                    <p className="tw-mb-0">{v.check_in_at ? fmtDateTime(v.check_in_at) : <span className="tw-text-gray-300">Not in</span>}</p>
                    <p className="tw-mb-0 tw-text-gray-500">{v.check_out_at ? fmtDateTime(v.check_out_at) : '—'}</p>
                </div>
            ),
        },
        {
            key: 'actions',
            header: 'Action',
            align: 'right',
            stopPropagation: true,
            render: (v) => (
                <div className="tw-inline-flex tw-items-center tw-gap-1">
                    {/* Which transitions exist is decided by the visit's status,
                        not by how it was created — a DIRECT_ENTRY society hands
                        back visits that are already checked in. */}
                    {canUpdate && availableActions(v).map(a => (
                        <button
                            key={a.key}
                            title={a.label}
                            disabled={tx.acting}
                            onClick={() => tx.run(a.key, v)}
                            className={`tw-p-1.5 tw-rounded disabled:tw-opacity-50 ${a.tone}`}
                        >
                            <Icon icon={a.icon} className="tw-text-base" />
                        </button>
                    ))}
                    <button
                        title="View"
                        onClick={() => goView(v)}
                        className="tw-p-1.5 tw-rounded hover:tw-bg-blue-50 tw-text-blue-600"
                    >
                        <Icon icon="mdi:eye-outline" className="tw-text-base" />
                    </button>
                    {canDelete && canCancelVisit(v) && (
                        <button
                            title="Cancel visit"
                            disabled={tx.acting}
                            onClick={() => tx.open('cancel', v)}
                            className="tw-p-1.5 tw-rounded hover:tw-bg-rose-50 tw-text-rose-600 disabled:tw-opacity-50"
                        >
                            <Icon icon="mdi:cancel" className="tw-text-base" />
                        </button>
                    )}
                </div>
            ),
        },
    ];

    const filters = (
        <>
            <div>
                <label className={labelCls}>Status</label>
                <SearchableDropdown
                    small
                    value={list.filters.status}
                    onChange={(v) => list.setFilter('status', v === 'all' ? '' : v)}
                    options={STATUS_OPTIONS}
                    placeholder="All statuses"
                />
            </div>

            <WingFlatPicker
                clearable
                required={false}
                societyId={list.societyId}
                wingId={list.filters.wingId}
                flatId={list.filters.flatId}
                onWingChange={(v) => list.setFilter('wingId', v)}
                onFlatChange={(v) => list.setFilter('flatId', v)}
                wingPlaceholder="Any wing"
                flatPlaceholder="Any flat"
            />

            <div>
                <label className={labelCls}>From</label>
                <input
                    type="date"
                    value={list.filters.fromDate}
                    onChange={e => list.setFilter('fromDate', e.target.value)}
                    className="tw-w-full tw-px-2.5 tw-py-1.5 tw-text-xs tw-border tw-border-gray-300 tw-rounded focus:tw-outline-none focus:tw-border-sky-500"
                />
            </div>
            <div>
                <label className={labelCls}>To</label>
                <input
                    type="date"
                    value={list.filters.toDate}
                    onChange={e => list.setFilter('toDate', e.target.value)}
                    className="tw-w-full tw-px-2.5 tw-py-1.5 tw-text-xs tw-border tw-border-gray-300 tw-rounded focus:tw-outline-none focus:tw-border-sky-500"
                />
            </div>
            <div className="tw-flex tw-items-end">
                <button
                    type="button"
                    onClick={list.resetFilters}
                    disabled={!list.hasFilters}
                    className="tw-w-full tw-flex tw-items-center tw-justify-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-gray-600 tw-border tw-border-gray-300 tw-bg-white tw-rounded hover:tw-bg-gray-50 disabled:tw-opacity-40"
                >
                    <Icon icon="mdi:filter-remove-outline" className="tw-text-base" />
                    Clear
                </button>
            </div>
        </>
    );

    return (
        <div className="tw-p-5 tw-bg-gradient-to-br tw-from-gray-50 tw-to-slate-50 tw-min-h-screen tw-space-y-4">
            <VmsListShell
                title="Visits"
                subtitle="Every entry logged at the gate, and where it is in the approval flow"
                itemLabel="visits"
                list={list}
                filters={filters}
                headerActions={canCreate && (
                    <button
                        onClick={() => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_VISITS_ADD)}
                        className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-2 tw-rounded-md tw-text-xs tw-font-medium tw-text-white tw-bg-sky-600 hover:tw-bg-sky-700"
                    >
                        <Icon icon="mdi:door-open" className="tw-text-base" />
                        Log Visit
                    </button>
                )}
            >
                <VmsTable
                    columns={columns}
                    rows={list.rows}
                    loading={list.loading}
                    loaderLabel="Loading visits..."
                    rowKey={(v) => v.visit_id}
                    onRowClick={goView}
                    empty={{
                        icon: 'mdi:door-closed',
                        filtered: list.hasFilters,
                        filteredTitle: 'No visits match these filters',
                        filteredHint: 'Try a wider date range or a different status.',
                        emptyTitle: 'No visits logged yet',
                        emptyHint: 'A visit is the actual entry event — logging one looks up or creates the visitor, then follows your society’s approval rules.',
                        onClear: list.resetFilters,
                    }}
                />
            </VmsListShell>

            <VisitTransitionDialogs tx={tx} />
        </div>
    );
};

export default Visits;
